/*
 * predictive-fuzzy.js — client-side typo-tolerant search fallback.
 *
 * Shopify's native search (predictive dropdown + /search page) does not
 * forgive most single-letter misspellings (e.g. "vitoria" for Vittoria,
 * "beychavale" for Beychevelle). This fallback fires ONLY when the native
 * search returns no products, so normal searches are never slowed down.
 *
 * It builds a compact product index from /products.json once (cached in
 * localStorage for 24h, fetched lazily on the first miss), then matches with
 * a diacritic-insensitive, first-letter-anchored Levenshtein so that queries
 * one or two letters off still find the right product. See also the Shopify
 * Search & Discovery synonyms — this complements them for arbitrary typos.
 * Added 2026-06-15.
 */
(function () {
  'use strict';

  if (window.VinoFuzzy) return; // guard against double-load

  /* v2 carries vendor, product type and tags as well as the title: the
     predictive dropdown filters Shopify's suggestions against them (see
     assets/component-predictive-search.js), and a v1 cache has no such
     fields. Bumping the key retires yesterday's cache instead of reading
     undefined out of it. v3 keeps the tags one by one as well (`tl`), so a
     multi-word synonym can be held to a single tag instead of to words
     gathered from several. */
  var IDX_KEY = 'vino_fuzzy_idx_v3';
  var TTL = 24 * 60 * 60 * 1000; // refresh the cached index daily
  /* Ceiling, not an expectation: paging stops at the first short page. It was
     16 (4000 products) and the catalogue passed it — 5014 published products on
     2026-08-21, so the last ~1000 could never be suggested. Kept well clear of
     the real number, since the miss is silent. */
  var MAX_PAGES = 40; // 40 * 250 = 10000 products ceiling
  var BATCH = 4; // product pages fetched at once when building the index
  var LOCALES = (window.KROWN && KROWN.settings && KROWN.settings.locales) || {};
  var PRODUCTS_TITLE = LOCALES.search_products_title || 'Products';

  var indexPromise = null;
  var pageInited = false;

  // Lowercase, strip diacritics, keep only [a-z0-9], collapse to single spaces.
  function norm(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Damerau-Levenshtein (optimal string alignment) with early-exit once the
  // running minimum exceeds max. Unlike plain Levenshtein, an adjacent
  // letter-swap (traminer -> tramienr) costs 1 edit, not 2 — better coverage
  // for transposition typos. prev2 holds the row two iterations back, used for
  // the transposition case.
  function lev(a, b, max) {
    var m = a.length, n = b.length;
    if (Math.abs(m - n) > max) return max + 1;
    var prev2 = null;
    var prev = [];
    for (var j = 0; j <= n; j++) prev[j] = j;
    for (var i = 1; i <= m; i++) {
      var cur = [i], best = i;
      for (var k = 1; k <= n; k++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(k - 1) ? 0 : 1;
        var v = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + cost);
        if (i > 1 && k > 1 &&
            a.charCodeAt(i - 1) === b.charCodeAt(k - 2) &&
            a.charCodeAt(i - 2) === b.charCodeAt(k - 1)) {
          v = Math.min(v, prev2[k - 2] + 1);
        }
        cur[k] = v;
        if (v < best) best = v;
      }
      if (best > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[n];
  }

  /* Fetched a batch at a time rather than one page after another. The index is
     ~9 pages of 250 products and each one is a heavy payload, so walking them
     in single file took 6-10 seconds — and since search went strict
     (assets/search-and-terms.js) this fallback is the front line for a
     misspelling, not a last resort: "Cabarnet" now has nothing to show until
     the index is up. Order does not matter here, only completeness. */
  /* {ok, products}, never a bare list: a page that failed and a page that came
     back short have to stay distinguishable. Shopify throttles these requests,
     and treating a throttled page as an empty one would end the walk early and
     then cache that half-catalogue for 24 hours. */
  function fetchPage(p) {
    return fetch('/products.json?limit=250&page=' + p)
      .then(function (r) {
        if (!r.ok) return { ok: false, products: [] };
        return r.json().then(function (d) { return { ok: true, products: (d && d.products) || [] }; });
      })
      .catch(function () { return { ok: false, products: [] }; });
  }

  /* -> {items, complete}. `complete` is false when a page could not be read
     even on a second try; the caller uses the index it has for this visit but
     does not store it, so the next visit builds a whole one instead of being
     served a gap for the rest of the day. */
  function fetchAll() {
    var items = [];
    var complete = true;

    function collect(ps) {
      ps.forEach(function (pr) {
        if (!pr.title || !pr.handle) return;
        var img = (pr.images && pr.images[0] && pr.images[0].src) || '';
        var price = (pr.variants && pr.variants[0] && pr.variants[0].price) || '';
        /* Tags are two or three words per product in this catalogue ("Biele
           víno", "Víno"), so carrying them costs almost nothing and lets the
           dropdown filter agree with the results page, which searches tags. */
        var tags = (pr.tags || []).join(' ');
        items.push({
          t: pr.title,
          h: pr.handle,
          img: img,
          p: price,
          v: pr.vendor || '',
          ty: pr.product_type || '',
          tg: tags,
          tl: pr.tags || [],
        });
      });
    }

    function batch(start) {
      var pages = [];
      for (var p = start; p < start + BATCH && p <= MAX_PAGES; p += 1) pages.push(p);
      if (!pages.length) return Promise.resolve();

      return Promise.all(pages.map(function (p) {
        return fetchPage(p).then(function (res) { return res.ok ? res : fetchPage(p); });
      })).then(function (results) {
        var more = true;
        results.forEach(function (res) {
          if (!res.ok) { complete = false; more = false; return; }
          collect(res.products);
          /* A page that comes back short is the last one — anything past it is
             empty, so there is nothing more to ask for. */
          if (res.products.length < 250) more = false;
        });
        if (more) return batch(start + BATCH);
      });
    }

    return batch(1).then(function () { return { items: items, complete: complete }; });
  }

  function prepare(items) {
    return items.map(function (it) {
      /* `toks` is the title alone, which is what the typo ranking below has
         always matched on. `stoks` adds vendor, type and tags — the same four
         structured fields the results page scopes its query to, so the dropdown
         can hold a suggestion to the same standard. */
      var structured = norm([it.t, it.v, it.ty, it.tg].join(' '));
      return {
        t: it.t,
        h: it.h,
        img: it.img,
        p: it.p,
        toks: norm(it.t).split(' ').filter(Boolean),
        stoks: structured.split(' ').filter(Boolean),
        /* The same fields value by value — title, vendor, type, each tag — for
           a multi-word synonym, which has to land inside one of them. */
        svals: [it.t, it.v, it.ty].concat(it.tl || []).map(norm).filter(Boolean)
          .map(function (v) { return v.split(' '); }),
      };
    });
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = new Promise(function (resolve) {
      try {
        var raw = localStorage.getItem(IDX_KEY);
        if (raw) {
          var cached = JSON.parse(raw);
          if (cached && cached.ts && (Date.now() - cached.ts) < TTL && cached.items) {
            resolve(prepare(cached.items));
            return;
          }
        }
      } catch (e) {}
      fetchAll()
        .then(function (res) {
          /* Only a whole catalogue is worth keeping for a day. */
          if (res.complete) {
            try { localStorage.setItem(IDX_KEY, JSON.stringify({ ts: Date.now(), items: res.items })); } catch (e) {}
          }
          resolve(prepare(res.items));
        })
        .catch(function () { resolve([]); });
    });
    return indexPromise;
  }

  /* The index by handle, and a synchronous peek at it.
   *
   * The dropdown cannot wait for a cold index — building it is seconds and the
   * customer is mid-word — so it filters only when the map is already in hand
   * and shows Shopify's answer untouched otherwise. warm() is what makes that
   * the common case: the first keystroke starts the build for the ones after
   * it, and the daily localStorage cache covers the rest of the visit. */
  var handleMap = null;

  function buildMap(idx) {
    var map = {};
    idx.forEach(function (entry) { map[entry.h] = entry; });
    return map;
  }

  function warm() {
    return loadIndex().then(function (idx) {
      if (!handleMap) handleMap = buildMap(idx);
      return handleMap;
    });
  }

  // Rank products against a typo'd query.
  // Each non-trivial query token must match some title token: exact, prefix
  // (>=3 chars), or within an edit budget AND sharing the same first letter
  // (typos rarely change the first letter — this is what kills false hits).
  // Bare single letters and unmatched year numbers are treated as optional.
  function rank(query, idx, limit) {
    var q = norm(query);
    if (q.length < 3) return [];
    var qt = q.split(' ').filter(Boolean);
    var hard = [], nums = [];
    qt.forEach(function (t) {
      if (/^\d+$/.test(t)) nums.push(t);
      else if (t.length >= 2) hard.push(t);
    });
    if (!hard.length) return [];

    var out = [];
    for (var i = 0; i < idx.length; i++) {
      var p = idx[i], score = 0, ok = true;
      for (var h = 0; h < hard.length; h++) {
        var t = hard[h];
        var allow = t.length <= 4 ? 1 : (t.length <= 7 ? 2 : 3);
        var best = 99;
        for (var j = 0; j < p.toks.length; j++) {
          var tt = p.toks[j];
          if (tt === t) { best = 0; break; }
          if (t.length >= 3 && tt.indexOf(t) === 0) { if (best > 0.3) best = 0.3; continue; }
          if (tt.charCodeAt(0) === t.charCodeAt(0)) {
            var d = lev(t, tt, allow);
            if (d <= allow && d < best) best = d;
          }
        }
        if (best === 99) { ok = false; break; }
        score += best;
      }
      if (!ok) continue;
      for (var k = 0; k < nums.length; k++) { if (p.toks.indexOf(nums[k]) !== -1) score -= 0.5; }
      out.push({ p: p, score: score });
    }
    out.sort(function (a, b) { return a.score - b.score || a.p.t.length - b.p.t.length; });
    var res = [];
    for (var r = 0; r < out.length && r < (limit || 6); r++) res.push(out[r].p);
    return res;
  }

  function money(p) {
    if (p === '' || p == null) return '';
    var n = parseFloat(p);
    if (isNaN(n)) return '';
    return n.toFixed(2).replace('.', ',') + ' €';
  }

  function esc(s) {
    return (s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  var VinoFuzzy = {
    warm: warm,
    /* null until the index is loaded — callers must handle that and not block. */
    handles: function () { return handleMap; },
    search: function (query, limit) {
      return loadIndex().then(function (idx) { return rank(query, idx, limit); });
    },

    // Product block for the predictive dropdown, mirroring the markup of
    // sections/helper-predictive-search.liquid so it inherits the same styling.
    dropdownHTML: function (products) {
      if (!products || !products.length) return '';
      var items = products.map(function (p) {
        var url = '/products/' + p.h;
        var thumb = p.img
          ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="80" height="80" style="width:100%;height:100%;object-fit:cover">'
          : '';
        return '<div class="search-item cart-item" data-js-search-item>' +
          '<a tabindex="-1" href="' + url + '" title="' + esc(p.t) + '" class="cart-item__thumbnail element--border-width-clamped element--border-radius">' + thumb + '</a>' +
          '<div class="cart-item__content"><div>' +
          (money(p.p) ? '<div class="cart-item__price"><span class="price">' + money(p.p) + '</span></div>' : '') +
          '<a tabindex="-1" href="' + url + '" class="cart-item__title"><span class="text-animation--underline-thin">' + esc(p.t) + '</span></a>' +
          '</div></div></div>';
      }).join('');
      return '<div class="search-block" data-js-fuzzy-block><span class="search-title">' + esc(PRODUCTS_TITLE) + '</span>' + items + '</div>';
    }
  };

  window.VinoFuzzy = VinoFuzzy;

  // Search results PAGE fallback — populate the [data-js-fuzzy-page] holder
  // that sections/main-search.liquid renders when the native search found
  // nothing.
  function initPage() {
    if (pageInited) return;
    var holder = document.querySelector('[data-js-fuzzy-page]');
    if (!holder) return;
    pageInited = true;
    var terms = (holder.getAttribute('data-terms') || '').trim();
    if (!terms) return;
    VinoFuzzy.search(terms, 12).then(function (products) {
      if (!products.length) return;
      var cards = products.map(function (p) {
        var url = '/products/' + p.h;
        var thumb = p.img
          ? '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '" loading="lazy" class="lazy-image--animation" style="width:100%;aspect-ratio:1/1.33;object-fit:cover;border-radius:8px">'
          : '';
        return '<div class="fuzzy-card" style="margin-bottom:16px">' +
          '<a href="' + url + '" title="' + esc(p.t) + '">' + thumb +
          '<div class="text-weight--bold" style="margin-top:8px"><span class="text-animation--underline-thin">' + esc(p.t) + '</span></div>' +
          (money(p.p) ? '<div class="price" style="margin-top:4px">' + money(p.p) + '</div>' : '') +
          '</a></div>';
      }).join('');
      var heading = holder.getAttribute('data-heading') || '';
      holder.innerHTML =
        (heading ? '<h2 class="h5 margin-bottom--regular">' + esc(heading) + '</h2>' : '') +
        '<div class="grid grid--layout grid-4 grid-portable-3 grid-lap-2 grid-tiny-1">' + cards + '</div>';
      holder.style.display = '';
    });
  }

  if (document.readyState !== 'loading') initPage();
  else document.addEventListener('DOMContentLoaded', initPage);
})();
